/* =========================================================================
   useDashboardSettings — precision, units, timeframe, settings popover
   ========================================================================= */

import { useContext, useState, useCallback } from 'react';
import { UnitSystem } from '../lib/dashboard-types';
import { DEFAULT_UNITS, TelemetryLayoutMode } from '../../../../../CommonComponents/telemetry';
import { CommandCenterStateProviderContext } from '../../../../context';

export function useDashboardSettings() {
  const context = useContext(CommandCenterStateProviderContext);

  const [localPrecision, setLocalPrecision] = useState(2);
  const [localUnits, setLocalUnits] = useState<UnitSystem>({ ...DEFAULT_UNITS } as UnitSystem);
  const [localTimeframe, setLocalTimeframeState] = useState(60);
  const [localCustomStartTime, setLocalCustomStartTime] = useState<number | null>(null);
  const [localLayoutMode, setLocalLayoutMode] = useState<TelemetryLayoutMode>('grid');
  const [localSettingsOpen, setLocalSettingsOpen] = useState(false);

  const precision = context ? context.telemetryPrecision : localPrecision;
  const setPrecision = context ? context.setTelemetryPrecision : setLocalPrecision;

  const units = context ? context.telemetryUnits : localUnits;
  const setUnits = context ? context.setTelemetryUnits : setLocalUnits;

  const timeframe = context ? context.telemetryTimeframe : localTimeframe;
  const setTimeframeState = context ? context.setTelemetryTimeframe : setLocalTimeframeState;

  const customStartTimeSec = context ? context.telemetryCustomStartTime : localCustomStartTime;
  const setCustomStartTimeSec = context ? context.setTelemetryCustomStartTime : setLocalCustomStartTime;

  const layoutMode = context ? context.telemetryLayoutMode : localLayoutMode;
  const setLayoutMode = context ? context.setTelemetryLayoutMode : setLocalLayoutMode;

  const settingsOpen = context ? context.telemetrySettingsOpen : localSettingsOpen;
  const setSettingsOpen = context ? context.setTelemetrySettingsOpen : setLocalSettingsOpen;

  const toggleSettings = useCallback(() => {
    setSettingsOpen((p) => !p);
  }, [setSettingsOpen]);

  const setTimeframe = useCallback((tf: number) => {
    setTimeframeState(tf);
  }, [setTimeframeState]);

  const setUnit = useCallback((key: string, value: string) => {
    setUnits((prev: UnitSystem): UnitSystem => ({ ...prev, [key]: value }));
  }, [setUnits]);

  return {
    precision,
    setPrecision,
    units,
    setUnits,
    setUnit,
    timeframe,
    setTimeframe,
    customStartTimeSec,
    setCustomStartTimeSec,
    layoutMode,
    setLayoutMode,
    settingsOpen,
    setSettingsOpen,
    toggleSettings,
  };
}

export default useDashboardSettings;
